// src/ErrorBoundary.js
import React from 'react';
import { isChunkLoadError, handleChunkError } from './utils/chunkRetry';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isChunkError: false };
  }
  
  static getDerivedStateFromError(error) {
    const isChunkError = isChunkLoadError(error);
    
    // If it's a chunk loading error, handle it immediately
    if (isChunkError) {
      handleChunkError(error);
      return { hasError: true, error, isChunkError: true };
    }
    
    return { hasError: true, error, isChunkError: false };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    
    // Check if this is a chunk loading error
    if (isChunkLoadError(error)) {
      console.warn("Chunk loading error detected - will reload page");
      this.setState({ isChunkError: true });
      return; // handleChunkError will handle the reload
    }
    
    // Check if this is a hydration error
    if (error.message && error.message.includes('Hydration')) {
      console.warn("Hydration error detected - this is usually a client/server mismatch");
    }
    
    this.setState({ errorInfo });
  }
  
  render() {
    if (this.state.hasError) {
      // If it's a chunk loading error, show a minimal loading message
      // since the page will reload automatically
      if (this.state.isChunkError) {
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                border: '2px solid #e5e7eb',
                borderTop: '2px solid #3b82f6',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#374151' }}>Loading...</h3>
            <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
              Please wait while we load the page
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        );
      }
      
      // For other errors, show the full error UI
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p>Please try again later.</p>
          {process.env.NODE_ENV === 'development' && (
            <details style={{ marginTop: '20px', textAlign: 'left' }}>
              <summary>Error Details (Development Only)</summary>
              <pre style={{ color: 'red', fontSize: '12px' }}>
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              marginTop: '20px', 
              padding: '10px 20px', 
              backgroundColor: '#dc2626', 
              color: 'white', 
              border: 'none', 
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children; 
  }
}
